import { useCallback, useEffect, useState } from 'react';

import type { ToggleValue, ToggleValues } from '@/store/feature-toggle';
import { cachedToggleRequest, fetchMultipleToggles, fetchSingleToggle, getProjectFromUrl, onCacheInvalidated, pendingRequests, resolvedToggles } from '@/store/feature-toggle';

// Debounce time for cache invalidation callbacks (ms)
const CACHE_INVALIDATION_DEBOUNCE = 500;

interface UseFeatureToggleOptions {
  key: string;
  autoFetch?: boolean;
  serverValue?: boolean | null;
}

interface UseFeatureToggleReturn {
  isEnabled: boolean;
  isLoading: boolean;
  error: string | null;
  checkFeature: () => Promise<void>;
}

interface UseMultipleFeatureTogglesOptions {
  keys: string[];
  autoFetch?: boolean;
  serverValues?: Record<string, boolean> | null;
}

interface UseMultipleFeatureTogglesReturn {
  toggles: Record<string, boolean>;
  isLoading: boolean;
  error: string | null;
  checkFeatures: () => Promise<void>;
}

export async function checkMultipleFeatureTogglesClient(keys: string[]): Promise<ToggleValues> {
  if (keys.length === 0) {
    return {};
  }

  const project = getProjectFromUrl();
  const environment = process.env.NEXT_PUBLIC_ENV === 'development' ? 'staging' : process.env.NEXT_PUBLIC_ENV || 'production';

  // Cache key for this exact set of toggles (order-independent).
  const batchRequestKey = `batch:${keys.sort().join(',')}:${project}:${environment}`;

  const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL;
  if (!apiBaseUrl) {
    // Return fallback values for all keys
    const fallbackResults: Record<string, boolean> = {};
    for (const key of keys) {
      fallbackResults[key] = false;
    }
    return fallbackResults;
  }

  return cachedToggleRequest(batchRequestKey, () => fetchMultipleToggles(keys, project, environment, apiBaseUrl));
}

export function useFeatureToggle({ key, autoFetch = true, serverValue = null }: UseFeatureToggleOptions): UseFeatureToggleReturn {
  const [isEnabled, setIsEnabled] = useState<boolean>(serverValue ?? false);
  const [isLoading, setIsLoading] = useState<boolean>(serverValue === null);
  const [error, setError] = useState<string | null>(null);

  const checkFeature = useCallback(async () => {
    if (!key) {
      setError('Key is required');
      return;
    }

    try {
      const project = getProjectFromUrl();
      const environment = process.env.NEXT_PUBLIC_ENV === 'development' ? 'staging' : process.env.NEXT_PUBLIC_ENV || 'production';
      const requestKey = `${key}:${project}:${environment}`;

      // Reuse the cached result while it's still within the TTL window.
      const pending = pendingRequests.get(requestKey);
      if (pending && Date.now() - pending.timestamp < pending.ttl) {
        const result = (await pending.promise) as ToggleValue;
        if (result !== null) {
          setIsEnabled(result);
        }
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL;
      if (!apiBaseUrl) {
        setError('API URL not configured');
        setIsEnabled(false);
        setIsLoading(false);
        return;
      }

      const result = await cachedToggleRequest(requestKey, () => fetchSingleToggle(key, project, environment, apiBaseUrl));
      // A failed request keeps what is on screen instead of switching the toggle off.
      if (result !== null) {
        setIsEnabled(result);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check feature toggle';
      setError(errorMessage);
      setIsEnabled(false);
    } finally {
      setIsLoading(false);
    }
  }, [key]);

  useEffect(() => {
    if (serverValue !== null) {
      setIsLoading(false);
      return;
    }

    if (autoFetch) {
      checkFeature();
    }
  }, [checkFeature, autoFetch, serverValue]);

  // Listen for cache invalidation and refetch with debounce
  useEffect(() => {
    const debounceTimerRef = { current: null as NodeJS.Timeout | null };

    const unsubscribe = onCacheInvalidated(() => {
      // Clear previous debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce the refetch to prevent cascading updates
      debounceTimerRef.current = setTimeout(() => {
        checkFeature();
      }, CACHE_INVALIDATION_DEBOUNCE);
    });

    return () => {
      unsubscribe();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [checkFeature]);

  return {
    isEnabled,
    isLoading,
    error,
    checkFeature,
  };
}

export function useMultipleFeatureToggles({ keys, autoFetch = true, serverValues = null }: UseMultipleFeatureTogglesOptions): UseMultipleFeatureTogglesReturn {
  const [toggles, setToggles] = useState<Record<string, boolean>>(serverValues ?? {});
  const [isLoading, setIsLoading] = useState<boolean>(serverValues === null);
  const [error, setError] = useState<string | null>(null);

  // `useState` reads `serverValues` only on mount, but a caller mounted in `_app` receives fresh
  // server values on every navigation. Merging them during render (not in an effect) keeps the
  // stale value from painting for a frame — the old home hero flashing before the new one.
  const serverValuesSignature = serverValues ? JSON.stringify(serverValues) : null;
  const [syncedSignature, setSyncedSignature] = useState(serverValuesSignature);
  if (serverValuesSignature !== syncedSignature) {
    setSyncedSignature(serverValuesSignature);
    if (serverValues) {
      setToggles((previous) => ({ ...previous, ...serverValues }));
    }
  }

  const checkFeatures = useCallback(async () => {
    if (!keys || keys.length === 0) {
      setError('Keys array is required and cannot be empty');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Merged, not replaced: this runs on every cache invalidation, and a failed refetch must not
      // wipe the values the server already resolved — the old home hero coming back mid-session.
      const results = resolvedToggles(await checkMultipleFeatureTogglesClient(keys));
      setToggles((previous) => ({ ...previous, ...results }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check feature toggles';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [keys]);

  useEffect(() => {
    if (serverValues !== null) {
      setIsLoading(false);
      return;
    }

    if (autoFetch) {
      checkFeatures();
    }
  }, [checkFeatures, autoFetch, serverValues]);

  // Listen for cache invalidation and refetch with debounce
  useEffect(() => {
    const debounceTimerRef = { current: null as NodeJS.Timeout | null };

    const unsubscribe = onCacheInvalidated(() => {
      // Clear previous debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce the refetch to prevent cascading updates
      debounceTimerRef.current = setTimeout(() => {
        checkFeatures();
      }, CACHE_INVALIDATION_DEBOUNCE);
    });

    return () => {
      unsubscribe();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [checkFeatures]);

  return {
    toggles,
    isLoading,
    error,
    checkFeatures,
  };
}
